import { api } from "../../api";

export type PentesterFeedbackDetail = {
  pentester_id: number;
  technical_depth_rating: number;
  accuracy_rating: number;
  communication_rating: number;
  business_context_rating: number;
  pentester?: {
    user_id: number;
    username: string;
    display_name: string;
    pfp?: string;
  };
};

export type ProgramFeedbackLog = {
  log_id: number;
  feedback_id: number;
  action_type: string;
  performedBy: {
    user_id: number;
    username: string;
    display_name: string;
  };
  notes?: string;
  createdAt: string;
};

export type ProgramFeedbackDetail = {
  feedback_id: number;
  program_id: number;
  run_id?: number;
  status: string;
  program: {
    program_id: number;
    program_title: string;
    expected_start_date: string;
    expected_end_date: string;
    user_id: number;
    scheduling_type?: string;
  };
  programRun?: {
    run_id: number;
    run_title: string;
    run_start_date: string;
    run_end_date: string;
  };
  businessUser: {
    user_id: number;
    username: string;
    display_name: string;
    email: string;
    company_id: number;
  };
  overall_program_rating?: number;
  overall_program_comments?: string;
  pentester_feedbacks: PentesterFeedbackDetail[];
  improvement_reasons?: string[];
  improvement_details?: string;
  next_step_option?: string;
  areas_needing_review?: string;
  requested_deadline?: string;
  logs?: ProgramFeedbackLog[];
  createdAt: string;
  updatedAt: string;
};

export type FeedbackListResponse = {
  feedbacks: ProgramFeedbackDetail[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
};

export type UpdateProgramDatesRequest = {
  feedback_id: number;
  program_id: number;
  run_id?: number;
  new_end_date: string;
  reason?: string;
};

export type UpdateFeedbackStatusRequest = {
  feedback_id: number;
  status: string;
  notes?: string;
};

export type RequestFeedbackRequest = {
  program_id: number;
  run_id?: number;
  business_user_id: number;
};

export const adminFeedbackApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get all program feedback with filters (Admin only)
     */
    getAllProgramFeedback: builder.query<
      FeedbackListResponse,
      {
        page?: number;
        limit?: number;
        rating_filter?: "low" | "high" | "all";
        action_required?: boolean;
        show_completed?: boolean;
        search?: string;
        company_user_ids?: string;
      }
    >({
      query: (params) => ({
        url: "/program-feedback/admin/all",
        method: "get",
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          rating_filter: params.rating_filter || "all",
          action_required: params.action_required ? "true" : undefined,
          show_completed: params.show_completed ? "true" : undefined,
          search: params.search || undefined,
          company_user_ids: params.company_user_ids || undefined
        }
      }),
      providesTags: ["AdminFeedback"]
    }),

    /**
     * Update program dates based on feedback (Admin only)
     */
    updateProgramDatesFromFeedback: builder.mutation<
      { message: string; program: any },
      UpdateProgramDatesRequest
    >({
      query: (data) => ({
        url: "/program-feedback/admin/update-program-dates",
        method: "post",
        data
      }),
      invalidatesTags: ["AdminFeedback"]
    }),

    /**
     * Request feedback from a business user (Admin only)
     */
    requestFeedback: builder.mutation<
      { message: string; data: any },
      RequestFeedbackRequest
    >({
      query: (data) => ({
        url: "/program-feedback/admin/request",
        method: "post",
        data
      }),
      invalidatesTags: ["AdminFeedback", "ProgramFeedback"]
    }),

    /**
     * Undo request feedback from a business user (Admin only)
     */
    undoRequestFeedback: builder.mutation<
      { message: string },
      RequestFeedbackRequest
    >({
      query: (data) => ({
        url: "/program-feedback/admin/undo-request",
        method: "post",
        data
      }),
      invalidatesTags: ["AdminFeedback", "ProgramFeedback"]
    }),

    /**
     * Update feedback status (resolve/complete) (Admin only)
     */
    updateFeedbackStatus: builder.mutation<
      { message: string; status: string },
      UpdateFeedbackStatusRequest
    >({
      query: (data) => ({
        url: "/program-feedback/admin/update-status",
        method: "post",
        data
      }),
      invalidatesTags: ["AdminFeedback"]
    })
  })
});

export const {
  useGetAllProgramFeedbackQuery,
  useUpdateProgramDatesFromFeedbackMutation,
  useRequestFeedbackMutation,
  useUndoRequestFeedbackMutation,
  useUpdateFeedbackStatusMutation
} = adminFeedbackApi;

